import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Home } from "lucide-react";
import { getCustomGame } from "../lib/storage";
import { findCeleb } from "../data/celebrities";
import { REASONS } from "../data/gameData";

const Bar = ({ label, value, accent }) => (
  <div>
    <div className="flex justify-between text-xs text-[#FDFBF7]/70 mb-1">
      <span className="tracking-[0.15em] uppercase font-semibold">{label}</span>
      <span className="font-display text-lg" style={{ color: accent }}>{value}%</span>
    </div>
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ duration: 1 }}
        className="h-full rounded-full"
        style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
      />
    </div>
  </div>
);

const reasonLabel = (id) => REASONS.find((r) => r.id === id)?.label || id;

export default function CustomComparison() {
  const nav = useNavigate();
  const { code } = useParams();
  const [game, setGame] = useState(null);

  useEffect(() => {
    setGame(getCustomGame(code));
  }, [code]);

  if (!game || !game.comparison) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <h1 className="font-display text-4xl tracking-tight">Comparison not ready</h1>
          <button onClick={() => nav("/home")} className="mt-6 rounded-full bg-[#FF107A] px-6 py-3 font-bold glow-pink">Home</button>
        </div>
      </div>
    );
  }

  const c = game.comparison;
  const mostRevealing = game.rounds.find((r) => r.id === c.mostRevealingRoundId);
  const biggestDiff = game.rounds.find((r) => r.id === c.biggestDifferenceRoundId);

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="cmp-back-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#00E5FF] font-semibold">Reveal</div>
          <h1 className="font-display text-5xl tracking-tight mt-2">Game Connection</h1>
          <p className="mt-2 text-sm text-[#FDFBF7]/70">
            {game.organizerName} predicted <span className="text-[#FF107A] font-bold">{c.correctChoicePredictions} of {c.totalRounds}</span> of your choices correctly.
          </p>
          <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/40">
            Game connection score — not a scientific compatibility test.
          </p>
        </motion.div>

        <div
          className="mt-6 rounded-3xl border border-[#FF107A]/30 p-6 text-center"
          style={{
            background: "radial-gradient(circle at 30% 20%, rgba(255,16,122,0.25), transparent 60%), #16141D",
          }}
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">Connection Score</div>
          <div className="font-display text-8xl leading-none tracking-tight text-[#FF107A] mt-1 glow-pink inline-block px-4">
            {c.connectionScore}
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-2">out of 100</div>
        </div>

        <div className="mt-6 space-y-4">
          <Bar label="Prediction Accuracy" value={c.predictionAccuracy} accent="#FF107A" />
          <Bar label="Choice Alignment" value={c.choiceAlignment} accent="#00E5FF" />
          <Bar label="Reason Alignment" value={c.reasonAlignment} accent="#A855F7" />
          <Bar label="Difficulty Alignment" value={c.difficultyAlignment} accent="#FFD700" />
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-[#16141D] p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold mb-2">Insights</div>
          <ul className="space-y-2 text-sm text-[#FDFBF7]/85">
            <li>
              <span className="text-[#FDFBF7]/50">Unexpected answers:</span>{" "}
              <span className="font-semibold">{c.unexpectedCount}</span>
            </li>
            <li>
              <span className="text-[#FDFBF7]/50">Strongest shared preference:</span>{" "}
              <span className="font-semibold">{c.sharedTopReason}</span>
            </li>
            {mostRevealing && (
              <li>
                <span className="text-[#FDFBF7]/50">Most revealing matchup:</span>{" "}
                <span className="font-semibold">
                  {findCeleb(mostRevealing.aId)?.name} vs {findCeleb(mostRevealing.bId)?.name}
                </span>
              </li>
            )}
            {biggestDiff && (
              <li>
                <span className="text-[#FDFBF7]/50">Biggest difference:</span>{" "}
                <span className="font-semibold">
                  {findCeleb(biggestDiff.aId)?.name} vs {findCeleb(biggestDiff.bId)?.name}
                </span>
              </li>
            )}
          </ul>
        </div>

        {/* Round-by-round reveal */}
        <div className="mt-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold mb-3">Round-by-round reveal</div>
          <div className="space-y-3">
            {game.rounds.map((r, idx) => {
              const p = game.participantAnswers.find((x) => x.roundId === r.id);
              const a = findCeleb(r.aId);
              const b = findCeleb(r.bId);
              const chosen = findCeleb(p?.selectedCelebrityId);
              const predicted = findCeleb(r.organizerPredictionId);
              const correct = r.organizerPredictionId === p?.selectedCelebrityId;
              return (
                <div key={r.id} data-testid={`compare-round-${idx}`} className="rounded-2xl border border-white/10 bg-[#16141D] p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg tracking-wide">
                      {a?.name} <span className="text-[#FF107A]">vs</span> {b?.name}
                    </div>
                    <span className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full ${correct ? "bg-[#00E5FF]/20 text-[#00E5FF]" : "bg-[#FF107A]/20 text-[#FF107A]"}`}>
                      {correct ? "Called it" : "Surprise"}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-[#FDFBF7]/70 grid grid-cols-2 gap-x-3 gap-y-1">
                    <div><span className="text-[#FDFBF7]/40">You picked:</span> <span className="font-semibold text-[#FDFBF7]">{chosen?.name}</span></div>
                    <div><span className="text-[#FDFBF7]/40">{game.organizerName} predicted:</span> <span className="font-semibold text-[#FDFBF7]">{predicted?.name}</span></div>
                    <div><span className="text-[#FDFBF7]/40">Your reasons:</span> <span className="font-semibold text-[#FDFBF7]">{(p?.reasons || []).map(reasonLabel).join(", ") || "—"}</span></div>
                    <div><span className="text-[#FDFBF7]/40">Their reasons:</span> <span className="font-semibold text-[#FDFBF7]">{(r.organizerReasons || []).map(reasonLabel).join(", ") || "—"}</span></div>
                  </div>
                  {r.organizerNote && (
                    <div className="mt-3 rounded-lg bg-[#A855F7]/10 border border-[#A855F7]/30 p-3">
                      <div className="text-[9px] tracking-[0.3em] uppercase text-[#A855F7] font-bold">Personal note</div>
                      <p className="mt-1 text-sm italic text-[#FDFBF7]/90">&ldquo;{r.organizerNote}&rdquo;</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button data-testid="cmp-home-btn" onClick={() => nav("/home")} className="mt-8 w-full rounded-full border-2 border-white/15 py-3 flex items-center justify-center gap-2">
          <Home size={16} /> Back to Home
        </button>
      </div>
    </div>
  );
}
